import { StorageAdapter } from './StorageAdapter';
import { DiagramRepository } from './DiagramRepository';
import {
    AppConfig,
    DIAGRAM_SCHEMA_VERSION,
    STORAGE_KEYS,
    VersionedData,
} from './types';
import Diagram from '@semantical/modules/diagram/Diagram';
import { Vocabulary } from '@semantical/modules/vocabulary';
import { KnowledgeGraph } from '@semantical/modules/knowledge-graph';
import { SchemaClass, ObjectProperty } from '@semantical/modules/ontology-model';
import type { OntologyEnabledBoundedContext } from '@semantical/modules/workspace';
import type { Individual } from '@semantical/modules/semantical-core';

export interface SaveResult {
    success: boolean;
    error?: string;
}

export interface LoadResult<T> {
    success: boolean;
    data?: T;
    discarded?: boolean;  // True if stored data was discarded due to version mismatch
    error?: string;
}

/**
 * High-level persistence service that handles versioning and coordinates
 * between storage adapters and repositories.
 *
 * Storage structure:
 * - semantical.diagrams: JSON array of diagram IDs
 * - semantical.diagram.{id}: Individual diagram data
 * - semantical.knowledgeGraph.{id}: KnowledgeGraph for each diagram
 * - semantical.appConfig: Global app settings
 */
export class PersistenceService {
    private storageAdapter: StorageAdapter;
    private diagramRepository: DiagramRepository;

    constructor(storageAdapter: StorageAdapter) {
        this.storageAdapter = storageAdapter;
        this.diagramRepository = new DiagramRepository();
    }

    // =========================================================================
    // Diagram Index Methods
    // =========================================================================

    /**
     * Get all diagram IDs from the index.
     */
    getDiagramIds(): string[] {
        try {
            const raw = this.storageAdapter.getItem(STORAGE_KEYS.DIAGRAM_INDEX);
            if (!raw) return [];
            return JSON.parse(raw) as string[];
        } catch {
            return [];
        }
    }

    /**
     * Add a diagram ID to the index.
     */
    private addDiagramToIndex(id: string): void {
        const ids = this.getDiagramIds();
        if (!ids.includes(id)) {
            ids.push(id);
            this.storageAdapter.setItem(STORAGE_KEYS.DIAGRAM_INDEX, JSON.stringify(ids));
        }
    }

    /**
     * Remove a diagram ID from the index.
     */
    private removeDiagramFromIndex(id: string): void {
        const ids = this.getDiagramIds().filter(i => i !== id);
        this.storageAdapter.setItem(STORAGE_KEYS.DIAGRAM_INDEX, JSON.stringify(ids));
    }

    /**
     * Check if a diagram exists by ID.
     */
    diagramExists(id: string): boolean {
        return this.getDiagramIds().includes(id);
    }

    // =========================================================================
    // Diagram Methods (ID-based)
    // =========================================================================

    /**
     * Save diagram to storage with versioning envelope.
     * Uses the diagram's ID for storage key.
     */
    saveDiagram(diagram: Diagram, domainId: string): SaveResult {
        try {
            const serialized = this.diagramRepository.serialize(diagram);
            const versioned: VersionedData<any> = {
                version: DIAGRAM_SCHEMA_VERSION,
                timestamp: Date.now(),
                domainId,
                data: serialized,
            };

            const key = STORAGE_KEYS.DIAGRAM_PREFIX + diagram.id;
            this.storageAdapter.setItem(key, JSON.stringify(versioned));
            this.addDiagramToIndex(diagram.id);

            console.debug('PersistenceService: Diagram saved', { id: diagram.id, version: DIAGRAM_SCHEMA_VERSION, domainId });
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveDiagram failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load diagram from storage by ID.
     * Returns discarded: true if stored data version doesn't match.
     *
     * @param diagramId - The diagram ID to load
     * @param knowledgeGraph - The KnowledgeGraph for resolving ABox individuals
     * @param boundedContext - Optional OntologyEnabledBoundedContext for resolving TBox individuals (SchemaIndividual)
     */
    loadDiagram(diagramId: string, knowledgeGraph: KnowledgeGraph, boundedContext?: OntologyEnabledBoundedContext): LoadResult<Diagram> {
        try {
            const key = STORAGE_KEYS.DIAGRAM_PREFIX + diagramId;
            const raw = this.storageAdapter.getItem(key);

            if (!raw) {
                console.debug('PersistenceService: No stored diagram found for ID', diagramId);
                return { success: true, data: undefined };
            }

            const versioned = JSON.parse(raw) as VersionedData<any>;

            // Version check - discard if mismatch
            if (versioned.version !== DIAGRAM_SCHEMA_VERSION) {
                console.debug(
                    `PersistenceService: Schema version mismatch (stored: ${versioned.version}, current: ${DIAGRAM_SCHEMA_VERSION}). Discarding stored data.`
                );
                this.storageAdapter.removeItem(key);
                this.removeDiagramFromIndex(diagramId);
                return { success: true, discarded: true };
            }

            const diagram = this.diagramRepository.deserialize(versioned.data, knowledgeGraph, boundedContext);

            if (!diagram) {
                return { success: false, error: 'Failed to deserialize diagram' };
            }

            console.debug('PersistenceService: Diagram loaded', {
                id: diagramId,
                version: versioned.version,
                domainId: versioned.domainId,
                timestamp: new Date(versioned.timestamp).toISOString(),
            });

            return { success: true, data: diagram };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.loadDiagram failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Get the domain ID for a stored diagram.
     */
    getDiagramDomainId(diagramId: string): string | undefined {
        try {
            const key = STORAGE_KEYS.DIAGRAM_PREFIX + diagramId;
            const raw = this.storageAdapter.getItem(key);
            if (!raw) return undefined;
            const versioned = JSON.parse(raw) as VersionedData<any>;
            return versioned.domainId;
        } catch {
            return undefined;
        }
    }

    /**
     * Get the datasetId from a stored diagram without full deserialization.
     * Returns the datasetId if present, null if explicitly null, or undefined if not stored.
     */
    getDiagramDatasetId(diagramId: string): string | null | undefined {
        try {
            const key = STORAGE_KEYS.DIAGRAM_PREFIX + diagramId;
            const raw = this.storageAdapter.getItem(key);
            if (!raw) return undefined;
            const versioned = JSON.parse(raw) as VersionedData<any>;
            return versioned.data?.datasetId;
        } catch {
            return undefined;
        }
    }

    /**
     * Delete a diagram and its associated knowledge graph.
     */
    deleteDiagram(diagramId: string): SaveResult {
        try {
            this.storageAdapter.removeItem(STORAGE_KEYS.DIAGRAM_PREFIX + diagramId);
            this.storageAdapter.removeItem(STORAGE_KEYS.KNOWLEDGE_GRAPH_PREFIX + diagramId);
            this.removeDiagramFromIndex(diagramId);
            console.debug('PersistenceService: Diagram deleted', { id: diagramId });
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.deleteDiagram failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Clear all persisted data.
     */
    clearAll(): void {
        // Remove all diagrams and their knowledge graphs
        for (const id of this.getDiagramIds()) {
            this.storageAdapter.removeItem(STORAGE_KEYS.DIAGRAM_PREFIX + id);
            this.storageAdapter.removeItem(STORAGE_KEYS.KNOWLEDGE_GRAPH_PREFIX + id);
        }
        // Clear the index
        this.storageAdapter.removeItem(STORAGE_KEYS.DIAGRAM_INDEX);
        console.debug('PersistenceService: All data cleared');
    }

    /**
     * Save app configuration to storage.
     */
    saveAppConfig(config: AppConfig): SaveResult {
        try {
            this.storageAdapter.setItem(STORAGE_KEYS.APP_CONFIG, JSON.stringify(config));
            console.debug('PersistenceService: App config saved', config);
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveAppConfig failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load app configuration from storage.
     */
    loadAppConfig(): LoadResult<AppConfig> {
        try {
            const raw = this.storageAdapter.getItem(STORAGE_KEYS.APP_CONFIG);

            if (!raw) {
                console.debug('PersistenceService: No stored app config found');
                return { success: true, data: undefined };
            }

            const config = JSON.parse(raw) as AppConfig;
            console.debug('PersistenceService: App config loaded', config);
            return { success: true, data: config };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.loadAppConfig failed:', message);
            return { success: false, error: message };
        }
    }

    // =========================================================================
    // Namespace Colors Methods
    // =========================================================================

    /**
     * Save namespace color customizations to storage.
     *
     * @param colors - Map of namespace IRI to hex color
     * @returns Save result
     */
    saveNamespaceColors(colors: Map<string, string>): SaveResult {
        try {
            const obj = Object.fromEntries(colors);
            this.storageAdapter.setItem(
                STORAGE_KEYS.NAMESPACE_COLORS,
                JSON.stringify(obj)
            );
            console.debug('PersistenceService: Namespace colors saved');
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveNamespaceColors failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load namespace color customizations from storage.
     *
     * @returns Map of namespace IRI to hex color
     */
    loadNamespaceColors(): Map<string, string> {
        try {
            const raw = this.storageAdapter.getItem(STORAGE_KEYS.NAMESPACE_COLORS);
            if (!raw) {
                console.debug('PersistenceService: No namespace colors found');
                return new Map();
            }
            const obj = JSON.parse(raw) as Record<string, string>;
            const colors = new Map<string, string>(Object.entries(obj));
            console.debug('PersistenceService: Namespace colors loaded', colors.size);
            return colors;
        } catch (error) {
            console.error('PersistenceService.loadNamespaceColors failed:', error);
            return new Map();
        }
    }

    // =========================================================================
    // Toolbox Preferences Methods
    // =========================================================================

    /**
     * Save Toolbox view mode preference to storage.
     *
     * @param mode - View mode ('hierarchical' or 'flat')
     * @returns Save result
     */
    saveToolboxViewMode(mode: 'hierarchical' | 'flat'): SaveResult {
        try {
            this.storageAdapter.setItem(STORAGE_KEYS.TOOLBOX_VIEW_MODE, mode);
            console.debug('PersistenceService: Toolbox view mode saved:', mode);
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveToolboxViewMode failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load Toolbox view mode preference from storage.
     *
     * @returns View mode ('hierarchical' or 'flat'), defaults to 'hierarchical'
     */
    loadToolboxViewMode(): 'hierarchical' | 'flat' {
        try {
            const mode = this.storageAdapter.getItem(STORAGE_KEYS.TOOLBOX_VIEW_MODE);
            if (mode === 'flat') {
                console.debug('PersistenceService: Toolbox view mode loaded: flat');
                return 'flat';
            }
            console.debug('PersistenceService: Toolbox view mode loaded: hierarchical (default)');
            return 'hierarchical';
        } catch (error) {
            console.error('PersistenceService.loadToolboxViewMode failed:', error);
            return 'hierarchical';
        }
    }

    /**
     * Save Toolbox group by ontology preference to storage.
     *
     * @param groupByOntology - Whether to group classes by ontology
     * @returns Save result
     */
    saveToolboxGroupByOntology(groupByOntology: boolean): SaveResult {
        try {
            this.storageAdapter.setItem(
                STORAGE_KEYS.TOOLBOX_GROUP_BY_ONTOLOGY,
                JSON.stringify(groupByOntology)
            );
            console.debug('PersistenceService: Toolbox group by ontology saved:', groupByOntology);
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveToolboxGroupByOntology failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load Toolbox group by ontology preference from storage.
     *
     * @returns Boolean indicating whether to group by ontology, defaults to false
     */
    loadToolboxGroupByOntology(): boolean {
        try {
            const value = this.storageAdapter.getItem(STORAGE_KEYS.TOOLBOX_GROUP_BY_ONTOLOGY);
            if (value) {
                const groupByOntology = JSON.parse(value);
                console.debug('PersistenceService: Toolbox group by ontology loaded:', groupByOntology);
                return groupByOntology;
            }
            console.debug('PersistenceService: Toolbox group by ontology loaded: false (default)');
            return false;
        } catch (error) {
            console.error('PersistenceService.loadToolboxGroupByOntology failed:', error);
            return false;
        }
    }

    // =========================================================================
    // URL Domain Methods
    // =========================================================================

    /**
     * Save URL-loaded domain URLs to storage.
     *
     * @param domains - Array of persisted URL domain entries
     * @returns Save result
     */
    saveUrlDomains(domains: Array<{ url: string; domainId: string; label: string; ns: string }>): SaveResult {
        try {
            this.storageAdapter.setItem(
                STORAGE_KEYS.URL_DOMAINS,
                JSON.stringify(domains)
            );
            console.debug('PersistenceService: URL domains saved', domains.length);
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveUrlDomains failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load URL-loaded domain URLs from storage.
     *
     * @returns Array of persisted URL domain entries
     */
    loadUrlDomains(): Array<{ url: string; domainId: string; label: string; ns: string }> {
        try {
            const raw = this.storageAdapter.getItem(STORAGE_KEYS.URL_DOMAINS);
            if (!raw) {
                console.debug('PersistenceService: No URL domains found');
                return [];
            }
            const domains = JSON.parse(raw) as Array<{ url: string; domainId: string; label?: string; ns?: string }>;
            // Filter out entries missing required fields (from older versions)
            const validDomains = domains.filter(d => d.label && d.ns) as Array<{ url: string; domainId: string; label: string; ns: string }>;
            console.debug('PersistenceService: URL domains loaded', validDomains.length);
            return validDomains;
        } catch (error) {
            console.error('PersistenceService.loadUrlDomains failed:', error);
            return [];
        }
    }

    /**
     * Add a URL domain to persistent storage.
     * Avoids duplicates based on URL.
     *
     * @param url - The URL from which the ontology was loaded
     * @param domainId - The generated domain ID
     * @param label - The domain label (from ontology metadata)
     * @param ns - The domain namespace
     * @returns Save result
     */
    addUrlDomain(url: string, domainId: string, label: string, ns: string): SaveResult {
        const domains = this.loadUrlDomains();
        // Avoid duplicates
        if (!domains.some(d => d.url === url)) {
            domains.push({ url, domainId, label, ns });
            return this.saveUrlDomains(domains);
        }
        console.debug('PersistenceService: URL domain already exists:', url);
        return { success: true };
    }

    /**
     * Remove a URL domain from persistent storage.
     *
     * @param url - The URL to remove
     * @returns Save result
     */
    removeUrlDomain(url: string): SaveResult {
        const domains = this.loadUrlDomains().filter(d => d.url !== url);
        return this.saveUrlDomains(domains);
    }

    // =========================================================================
    // KnowledgeGraph (ABox) Methods (ID-based)
    // =========================================================================

    /**
     * Save KnowledgeGraph (ABox) to storage with versioning envelope.
     * Uses the diagram ID for storage key.
     */
    saveKnowledgeGraph(diagramId: string, knowledgeGraph: KnowledgeGraph, domainId: string): SaveResult {
        try {
            const versioned: VersionedData<any> = {
                version: DIAGRAM_SCHEMA_VERSION,
                timestamp: Date.now(),
                domainId,
                data: knowledgeGraph.toJSON(),
            };

            const key = STORAGE_KEYS.KNOWLEDGE_GRAPH_PREFIX + diagramId;
            this.storageAdapter.setItem(key, JSON.stringify(versioned));

            console.debug('PersistenceService: KnowledgeGraph saved', { diagramId, domainId });
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveKnowledgeGraph failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load KnowledgeGraph (ABox) from storage by diagram ID.
     * Returns discarded: true if stored data version doesn't match.
     *
     * @param diagramId - The diagram ID to load the knowledge graph for
     * @param vocabulary - The vocabulary for concept resolution
     * @param schemaClassResolver - Function to resolve SchemaClass from URI (for individuals)
     * @param objectPropertyResolver - Function to resolve ObjectProperty from URI (for assertions)
     * @param tboxIndividualResolver - Function to resolve TBox individuals (SchemaIndividual) from URI (for relationship objects)
     */
    loadKnowledgeGraph(
        diagramId: string,
        vocabulary: Vocabulary,
        schemaClassResolver?: (uri: string) => SchemaClass | undefined,
        objectPropertyResolver?: (uri: string) => ObjectProperty | undefined,
        tboxIndividualResolver?: (uri: string) => Individual | undefined
    ): LoadResult<KnowledgeGraph> {
        try {
            const key = STORAGE_KEYS.KNOWLEDGE_GRAPH_PREFIX + diagramId;
            const raw = this.storageAdapter.getItem(key);

            if (!raw) {
                console.debug('PersistenceService: No stored KnowledgeGraph found for diagram', diagramId);
                return { success: true, data: undefined };
            }

            const versioned = JSON.parse(raw) as VersionedData<any>;

            // Version check - discard if mismatch
            if (versioned.version !== DIAGRAM_SCHEMA_VERSION) {
                console.debug(
                    `PersistenceService: KnowledgeGraph schema version mismatch (stored: ${versioned.version}, current: ${DIAGRAM_SCHEMA_VERSION}). Discarding stored data.`
                );
                this.storageAdapter.removeItem(key);
                return { success: true, discarded: true };
            }

            const knowledgeGraph = KnowledgeGraph.fromJSON(
                versioned.data,
                vocabulary,
                schemaClassResolver,
                objectPropertyResolver,
                tboxIndividualResolver
            );

            console.debug('PersistenceService: KnowledgeGraph loaded', {
                diagramId,
                version: versioned.version,
                domainId: versioned.domainId,
                timestamp: new Date(versioned.timestamp).toISOString(),
            });

            return { success: true, data: knowledgeGraph };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.loadKnowledgeGraph failed:', message);
            return { success: false, error: message };
        }
    }

    // =========================================================================
    // Active Context Methods
    // =========================================================================

    /**
     * Save the active bounded context ID.
     */
    saveActiveContextId(contextId: string): SaveResult {
        try {
            this.storageAdapter.setItem(STORAGE_KEYS.ACTIVE_CONTEXT_ID, contextId);
            console.debug('PersistenceService: Saved active context ID:', contextId);
            return { success: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error('PersistenceService.saveActiveContextId failed:', message);
            return { success: false, error: message };
        }
    }

    /**
     * Load the active bounded context ID.
     * Returns null if no context ID is stored.
     */
    loadActiveContextId(): string | null {
        try {
            const contextId = this.storageAdapter.getItem(STORAGE_KEYS.ACTIVE_CONTEXT_ID);
            if (contextId) {
                console.debug('PersistenceService: Loaded active context ID:', contextId);
            }
            return contextId;
        } catch (error) {
            console.error('PersistenceService.loadActiveContextId failed:', error);
            return null;
        }
    }

    /**
     * Clear the active bounded context ID.
     */
    clearActiveContextId(): void {
        try {
            this.storageAdapter.removeItem(STORAGE_KEYS.ACTIVE_CONTEXT_ID);
            console.debug('PersistenceService: Cleared active context ID');
        } catch (error) {
            console.error('PersistenceService.clearActiveContextId failed:', error);
        }
    }
}
